import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Euro, Plus, TrendingUp, Fuel, Car, ParkingSquare, AlertTriangle,
  Receipt, Trash2, FileText, Loader2, ChevronDown
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { formatCurrency, formatDate } from "../../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const COST_CATEGORIES = [
  { value: "maintenance", label: "Entretien", icon: Car },
  { value: "fuel", label: "Carburant", icon: Fuel },
  { value: "toll", label: "Péages", icon: Receipt },
  { value: "parking", label: "Parking", icon: ParkingSquare },
  { value: "fine", label: "Amendes", icon: AlertTriangle },
  { value: "insurance", label: "Assurance", icon: FileText },
  { value: "claim_deductible", label: "Franchise sinistre", icon: AlertTriangle },
  { value: "other", label: "Autre", icon: Receipt },
];

const CATEGORY_COLORS = {
  rent: "bg-blue-100 text-blue-800",
  maintenance: "bg-amber-100 text-amber-800",
  fuel: "bg-green-100 text-green-800",
  toll: "bg-purple-100 text-purple-800",
  parking: "bg-cyan-100 text-cyan-800",
  fine: "bg-red-100 text-red-800",
  insurance: "bg-indigo-100 text-indigo-800",
  claim_deductible: "bg-orange-100 text-orange-800",
  other: "bg-slate-100 text-slate-800",
};

export default function CostsTab({ vehicleId }) {
  const [loading, setLoading] = useState(true);
  const [costs, setCosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    category: "fuel",
    date: new Date().toISOString().split("T")[0],
    amount_ttc: "",
    amount_ht: "",
    description: "",
    invoice_number: "",
    supplier: "",
    mileage_at_cost: "",
  });
  
  const token = localStorage.getItem("token");

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    try {
      const [costsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/fleet/vehicles/${vehicleId}/costs`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/fleet/vehicles/${vehicleId}/costs/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCosts(costsRes.data.costs || []);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error("Error fetching costs:", err);
      toast.error("Erreur lors du chargement des coûts");
    } finally {
      setLoading(false);
    }
  }, [vehicleId, token]);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  const handleSubmit = async () => {
    if (!form.amount_ttc || parseFloat(form.amount_ttc) <= 0) {
      toast.error("Montant TTC requis");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/fleet/vehicles/${vehicleId}/costs`,
        {
          ...form,
          amount_ttc: parseFloat(form.amount_ttc),
          amount_ht: form.amount_ht ? parseFloat(form.amount_ht) : null,
          mileage_at_cost: form.mileage_at_cost ? parseInt(form.mileage_at_cost) : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Coût ajouté");
      setShowModal(false);
      setForm({
        category: "fuel",
        date: new Date().toISOString().split("T")[0],
        amount_ttc: "",
        amount_ht: "",
        description: "",
        invoice_number: "",
        supplier: "",
        mileage_at_cost: "",
      });
      fetchCosts();
    } catch (err) {
      console.error("Error creating cost:", err);
      toast.error("Erreur lors de l'ajout");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (costId) => {
    if (!window.confirm("Supprimer ce coût ?")) return;
    
    try {
      await axios.delete(
        `${API}/fleet/vehicles/${vehicleId}/costs/${costId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Coût supprimé");
      fetchCosts();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const categoryTotals = summary?.by_category || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Suivi des coûts</h3>
          <p className="text-sm text-slate-500">
            Loyers, entretien, carburant, péages et autres frais
          </p>
        </div>
        <Button onClick={() => setShowModal(true)} data-testid="add-cost-btn">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un coût
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total cumulé</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(summary?.total_ttc || 0)}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Euro className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Loyers cumulés</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(summary?.rent_total || 0)}
                </p>
                <p className="text-xs text-slate-400">{summary?.rent_months || 0} mois</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Coût / km</p>
                <p className="text-2xl font-bold text-slate-900">
                  {summary?.cost_per_km?.toFixed(3) || "0.000"} €
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Kilométrage</p>
                <p className="text-2xl font-bold text-slate-900">
                  {(summary?.current_mileage || 0).toLocaleString("fr-FR")} km
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Car className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown by category */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Répartition par catégorie</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(categoryTotals).map(([cat, amount]) => (
              <div key={cat} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <span className="text-sm capitalize">
                  {COST_CATEGORIES.find(c => c.value === cat)?.label || cat}
                </span>
                <Badge className={CATEGORY_COLORS[cat] || CATEGORY_COLORS.other}>
                  {formatCurrency(amount)}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Costs Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Historique des coûts</CardTitle>
        </CardHeader>
        <CardContent>
          {costs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p>Aucun coût enregistré (hors loyers)</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead className="text-right">Montant TTC</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costs.map((cost) => (
                  <TableRow key={cost.id}>
                    <TableCell>{formatDate(cost.date)}</TableCell>
                    <TableCell>
                      <Badge className={CATEGORY_COLORS[cost.category] || CATEGORY_COLORS.other}>
                        {cost.category_label}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {cost.description || "-"}
                    </TableCell>
                    <TableCell>{cost.supplier || "-"}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(cost.amount_ttc)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(cost.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Cost Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un coût</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Catégorie *</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COST_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant TTC *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.amount_ttc}
                  onChange={(e) => setForm({ ...form, amount_ttc: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Montant HT</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.amount_ht}
                  onChange={(e) => setForm({ ...form, amount_ht: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Fournisseur</Label>
              <Input
                placeholder="Ex: TotalEnergies"
                value={form.supplier}
                onChange={(e) => setForm({ ...form, supplier: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Ex: Plein carburant..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>N° facture</Label>
                <Input
                  placeholder="Ex: FAC-2024-001"
                  value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Kilométrage</Label>
                <Input
                  type="number"
                  placeholder="Ex: 52000"
                  value={form.mileage_at_cost}
                  onChange={(e) => setForm({ ...form, mileage_at_cost: e.target.value })}
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Ajouter
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
